'use client';

import '@/infrastructure/persistence/managers/LoggerManager';

import { UiAccessProvider } from '@umodeapporg/ui/components/UI/access/UiAccessContext';
import ToastHost from '@umodeapporg/ui/components/UI/ToastHost';
import { configureUiLogger } from '@umodeapporg/ui/helpers/uiLogger';
import { configureUiPreferences } from '@umodeapporg/ui/helpers/uiPreferences';
import { MotionConfig } from 'motion/react';
import { Suspense, useEffect } from 'react';
import { Slide, ToastContainer } from 'react-toastify';

import { UiNextNavigationProvider } from '@/components/navigation/UiNextNavigationProvider';
import { ThemeProvider, useTheme } from '@/components/theme/ThemeProvider';
import { i18nManager } from '@/infrastructure/persistence/managers/I18nManager';
import { localStorageManager } from '@/infrastructure/persistence/managers/LocalStorageManager';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';

i18nManager.initialize();
configureUiLogger(logger);
configureUiPreferences({
  storage: {
    read: <T,>(key: string) => localStorageManager.getItem<T>(key as never),
    write: <T,>(key: string, value: T) => localStorageManager.setItem<T>(key as never, value),
  },
});

const allowEverything = () => true;

function Toasts() {
  const { resolvedTheme } = useTheme();
  return (
    <>
      <ToastHost />
      <ToastContainer
        position="bottom-center"
        theme={resolvedTheme}
        transition={Slide}
        hideProgressBar={false}
        closeOnClick={false}
        pauseOnHover
        autoClose={5000}
      />
    </>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    i18nManager.syncStoredLanguage();
  }, []);

  return (
    /* `never` is deliberate: real motion reduction lives in the CSS layer of base.css,
       which shortens and flattens instead of deleting (see the lib's MOTION.md). */
    <MotionConfig reducedMotion="never">
      <ThemeProvider>
        <Suspense fallback={null}>
          <UiNextNavigationProvider>
            <UiAccessProvider check={allowEverything}>
              {children}
              <Toasts />
            </UiAccessProvider>
          </UiNextNavigationProvider>
        </Suspense>
      </ThemeProvider>
    </MotionConfig>
  );
}
